'use client';

import { useEffect } from 'react';
import { useGameStore } from '@/features/games/shared/store/game-store';
import { TIME_CONSTANTS_IN_MS } from '@/shared/constants/time';
import { useTranslations } from 'next-intl';

type PromotionsInfoProps = {
  skus: string[];
};
export function PromotionsInfo({ skus }: PromotionsInfoProps) {
  const fetchPrices = useGameStore((s) => s.fetchPrices);
  const promotions = useGameStore((s) => s.promotions);
  const prices = useGameStore((s) => s.prices);
  const t = useTranslations('Games');

  useEffect(() => {
    if (!skus.length) return;

    let isActive = true;
    let timeoutId: number | undefined;
    let lastAttemptAt = 0;

    const scheduleNextTick = () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      const { timestamps } = useGameStore.getState();
      const oldestTimestamp = skus.reduce(
        (min, sku) => Math.min(min, timestamps[sku] ?? 0),
        Number.POSITIVE_INFINITY,
      );
      const now = Date.now();
      const oldestIsUsable =
        oldestTimestamp > 0 && now - oldestTimestamp < TIME_CONSTANTS_IN_MS.TEN_MINUTES;
      const referenceTime = oldestIsUsable ? oldestTimestamp : lastAttemptAt;
      const msUntilStale = Math.max(0, TIME_CONSTANTS_IN_MS.TEN_MINUTES - (now - referenceTime));
      timeoutId = window.setTimeout(tick, msUntilStale);
    };

    const tick = () => {
      lastAttemptAt = Date.now();
      void Promise.allSettled(skus.map((sku) => fetchPrices(sku))).then(() => {
        if (isActive) scheduleNextTick();
      });
    };

    tick();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      isActive = false;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [skus, fetchPrices]);

  const hasAnyPromotion = skus.some((sku) => {
    return Boolean(promotions?.[sku] || prices?.[sku]?.promotion);
  });

  if (!hasAnyPromotion) return null;

  return <p className='text-text-subtle mt-4 text-sm'>* {t('promotionInfo')}</p>;
}
